import * as THREE from 'three';
import { markOverlay } from './edges';
import { axisColors } from './palette';
import type { AxisColors } from './palette';
import { ScreenLabel } from './screenLabel';

export type DimensionAxis = 'x' | 'y' | 'z';

export const DIMENSION_AXES: readonly DimensionAxis[] = ['x', 'y', 'z'];

const TIP_SCALE = 0.035;
const TIP_RADIUS = 0.35;

interface Guide {
  line: THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial>;
  tips: THREE.Mesh<THREE.ConeGeometry, THREE.MeshBasicMaterial>[];
  label: ScreenLabel;
  middle: THREE.Vector3;
}

const UP = new THREE.Vector3(0, 1, 0);

/** Arrows along the part's three extents, from its front-right-bottom corner, with their lengths. */
export class DimensionGuides {
  readonly object = new THREE.Group();
  private readonly guides: Record<DimensionAxis, Guide>;
  private shown = false;
  private hasBox = false;

  constructor(isDark: boolean) {
    const colors: AxisColors = axisColors(isDark);
    const cone = new THREE.ConeGeometry(TIP_RADIUS, 1, 16);
    this.guides = Object.fromEntries(
      DIMENSION_AXES.map((axis) => {
        const material = new THREE.MeshBasicMaterial({ color: colors[axis], toneMapped: false });
        const line = new THREE.Line(
          new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3()]),
          new THREE.LineBasicMaterial({ color: colors[axis], toneMapped: false }),
        );
        const tips = [new THREE.Mesh(cone, material), new THREE.Mesh(cone, material)];
        this.object.add(markOverlay(line), ...tips.map((tip) => markOverlay(tip)));
        return [axis, { line, tips, label: new ScreenLabel(), middle: new THREE.Vector3() }];
      }),
    ) as Record<DimensionAxis, Guide>;
    this.object.visible = false;
  }

  attachLabel(axis: DimensionAxis, element: HTMLElement | null): void {
    this.guides[axis].label.attach(element);
  }

  set(box: THREE.Box3 | null): void {
    this.hasBox = box !== null && !box.isEmpty();
    if (box && this.hasBox) this.build(box);
    this.refresh();
  }

  show(shown: boolean): void {
    this.shown = shown;
    this.refresh();
  }

  update(camera: THREE.Camera, width: number, height: number): void {
    const visible = this.object.visible;
    for (const axis of DIMENSION_AXES) {
      const guide = this.guides[axis];
      guide.label.place(visible && guide.line.visible ? guide.middle : null, camera, width, height);
    }
  }

  dispose(): void {
    this.object.removeFromParent();
    const geometries = new Set<THREE.BufferGeometry>();
    const materials = new Set<THREE.Material>();
    this.object.traverse((child) => {
      const drawable = child as THREE.Mesh;
      if (!drawable.geometry) return;
      geometries.add(drawable.geometry);
      materials.add(drawable.material as THREE.Material);
    });
    geometries.forEach((geometry) => geometry.dispose());
    materials.forEach((material) => material.dispose());
  }

  private refresh(): void {
    this.object.visible = this.shown && this.hasBox;
  }

  private build(box: THREE.Box3): void {
    const corner = new THREE.Vector3(box.max.x, box.min.y, box.min.z);
    const ends: Record<DimensionAxis, THREE.Vector3> = {
      x: new THREE.Vector3(box.min.x, box.min.y, box.min.z),
      y: new THREE.Vector3(box.max.x, box.max.y, box.min.z),
      z: new THREE.Vector3(box.max.x, box.min.y, box.max.z),
    };
    const tipSize = box.getSize(new THREE.Vector3()).length() * TIP_SCALE;
    for (const axis of DIMENSION_AXES) {
      const { line, tips, middle } = this.guides[axis];
      const end = ends[axis];
      const spans = end.distanceTo(corner) > 0;
      line.visible = spans;
      tips.forEach((tip) => {
        tip.visible = spans;
      });
      if (!spans) continue;
      line.geometry.setFromPoints([corner, end]);
      line.geometry.computeBoundingSphere();
      middle.addVectors(corner, end).multiplyScalar(0.5);
      const direction = end.clone().sub(corner).normalize();
      tips.forEach((tip, i) => {
        const outward = i === 0 ? direction.clone().negate() : direction;
        tip.position.copy(i === 0 ? corner : end).addScaledVector(outward, -tipSize / 2);
        tip.quaternion.setFromUnitVectors(UP, outward);
        tip.scale.setScalar(tipSize);
      });
    }
  }
}
